import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useSearchParams } from "react-router-dom";
import "./editItem.css";

import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import IsLoading from "../../../../../IsLoading";

 

const createEditableState = (data = {}) => ({
  itemType: data?.itemType || "Product",
  sku: data?.sku || "",
  barcode: data?.barcode || "",
  qrcode: data?.qrcode || "",
  name: data?.name || "",
  brand: data?.brand || "",
  assignedTo: data?.assignedTo || "INVENTORY_ITEM",
  description: data?.description || "",

  categoryId: data?.categoryId || "",
  supplierId: data?.supplierId || "",

  units: {
    baseUnit: data?.units?.baseUnit || "",
    purchaseUnit: data?.units?.purchaseUnit || "",
    conversionRate: data?.units?.conversionRate ?? "",
    salesUnit: data?.units?.salesUnit || "",
  },

  pricing: {
    costPrice: data?.pricing?.costPrice ?? "",
    sellingPrice: data?.pricing?.sellingPrice ?? "",
    taxRate: data?.pricing?.taxRate ?? "",
    currency: data?.pricing?.currency || "NGN",
  },

  dimensions: {
    unitsOfMeasure:
      data?.dimensions?.unitsOfMeasure || "",
    weight: data?.dimensions?.weight ?? "",
    length: data?.dimensions?.length ?? "",
    width: data?.dimensions?.width ?? "",
    height: data?.dimensions?.height ?? "",
  },

  images: Array.isArray(data?.images)
    ? [...data.images]
    : [],

  trackInventory:
    data?.trackInventory ?? true,

  status:
    data?.status || "active",

  inventories:
    Array.isArray(data?.inventories)
      ? data.inventories.map((inventory) => ({
          inventoryId:
            inventory?.inventoryId || "",

          stock: {
            reorderLevel:
              inventory?.stock?.reorderLevel ?? "",

            reorderQuantity:
              inventory?.stock?.reorderQuantity ?? "",

            minLevel:
              inventory?.stock?.minLevel ?? "",
          },

          batchTracking: {
            batchTracking:
              inventory?.batchTracking?.batchTracking ??
              false,

            expiryTracking:
              inventory?.batchTracking?.expiryTracking ??
              false,

            trackingMethod:
              inventory?.batchTracking?.trackingMethod ||
              "FEFO",
          },
        }))
      : [],
});
const buildEditItemPayload = (formData) => {
  return {
    itemType: formData.itemType,
    sku: formData.sku,
    barcode: formData.barcode,
    qrcode: formData.qrcode,

    name: formData.name,
    brand: formData.brand,
    assignedTo: formData.assignedTo,
    description: formData.description,

    categoryId: formData.categoryId,
    supplierId: formData.supplierId,

    units: {
      baseUnit: formData.units.baseUnit,
      purchaseUnit: formData.units.purchaseUnit,
      conversionRate:
        formData.units.conversionRate === ""
          ? null
          : Number(formData.units.conversionRate),
      salesUnit: formData.units.salesUnit,
    },

    pricing: {
      costPrice:
        formData.pricing.costPrice === ""
          ? 0
          : Number(formData.pricing.costPrice),

      sellingPrice:
        formData.pricing.sellingPrice === ""
          ? 0
          : Number(formData.pricing.sellingPrice),

      taxRate:
        formData.pricing.taxRate === ""
          ? null
          : Number(formData.pricing.taxRate),

      currency: formData.pricing.currency,
    },

    dimensions: {
      unitsOfMeasure: formData.dimensions.unitsOfMeasure,

      weight:
        formData.dimensions.weight === ""
          ? null
          : Number(formData.dimensions.weight),

      length:
        formData.dimensions.length === ""
          ? null
          : Number(formData.dimensions.length),

      width:
        formData.dimensions.width === ""
          ? null
          : Number(formData.dimensions.width),

      height:
        formData.dimensions.height === ""
          ? null
          : Number(formData.dimensions.height),
    },

    trackInventory: formData.trackInventory,

    status: formData.status,

    inventories: formData.inventories.map((inventory) => ({
      inventoryId: inventory.inventoryId,

      stock: {
        minLevel:
          inventory.stock.minLevel === ""
            ? null
            : Number(inventory.stock.minLevel),

        reorderLevel:
          inventory.stock.reorderLevel === ""
            ? null
            : Number(inventory.stock.reorderLevel),

        reorderQuantity:
          inventory.stock.reorderQuantity === ""
            ? null
            : Number(inventory.stock.reorderQuantity),
      },

      batchTracking: {
        batchTracking: inventory.batchTracking.batchTracking,

        expiryTracking: inventory.batchTracking.expiryTracking,

        trackingMethod: inventory.batchTracking.trackingMethod,
      },
    })),

    images: formData.images,
  };
};

const EditItem = ({ item, onCancel, onSave, setAlert }) => {
  const companyData = useSelector(
    (state) => state.clientFunction?.companiesDB?.clientData,
  );
  
  const [loading, setLoading] = useState(true);

  const [itemsData, setItemsData] = useState(null);

  const [formData, setFormData] = useState(null);

  const [errors, setErrors] = useState({});

  const [isDirty, setIsDirty] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const [searchParams] = useSearchParams();

   
  const [activeSections, setActiveSections] = useState({
    basic: true,
    identification: true,
    pricing: true,
    units: false,
    dimensions: false,
    inventory: true,
    images: false,
  });
 
 
  /* =========================================================
     GENERIC FIELD UPDATE
  ========================================================= */

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    const finalValue = type === "checkbox" ? checked : value;

    setFormData((previous) => ({
      ...previous,
      [name]: finalValue,
    }));

    setIsDirty(true);

    setErrors((previous) => ({
      ...previous,
      [name]: "",
    }));
  };

  /* =========================================================
     NESTED FIELD UPDATE
  ========================================================= */

  const handleNestedChange = (section, field, value) => {
    setFormData((previous) => ({
      ...previous,

      [section]: {
        ...previous[section],
        [field]: value,
      },
    }));

    setIsDirty(true);

    setErrors((previous) => ({
      ...previous,
      [`${section}.${field}`]: "",
    }));
  };

  /* =========================================================
     INVENTORY FIELD UPDATE
  ========================================================= */

  const handleInventoryChange = (inventoryIndex, section, field, value) => {
    setFormData((previous) => {
      const inventories = [...previous.inventories];

      inventories[inventoryIndex] = {
        ...inventories[inventoryIndex],

        [section]: {
          ...inventories[inventoryIndex][section],
          [field]: value,
        },
      };

      return {
        ...previous,
        inventories,
      };
    });

    setIsDirty(true);
  };

  /* =========================================================
     SECTION TOGGLE
  ========================================================= */

  const toggleSection = (section) => {
    setActiveSections((previous) => ({
      ...previous,
      [section]: !previous[section],
    }));
  };

  /* =========================================================
     RESET
  ========================================================= */

  const handleReset = () => {
    setFormData(createEditableState(itemsData));

    setErrors({});
    setIsDirty(false);
  };

  /* =========================================================
     VALIDATION
  ========================================================= */

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.name.trim()) {
      nextErrors.name = "Item name is required.";
    }

    if (!formData.sku.trim()) {
      nextErrors.sku = "SKU is required.";
    }

    if (
      formData.pricing.sellingPrice === "" ||
      Number(formData.pricing.sellingPrice) < 0
    ) {
      nextErrors["pricing.sellingPrice"] = "Enter a valid selling price.";
    }

    if (
      formData.pricing.costPrice === "" ||
      Number(formData.pricing.costPrice) < 0
    ) {
      nextErrors["pricing.costPrice"] = "Enter a valid cost price.";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  /* =========================================================
     SAVE HANDLER
     
     Axios will be connected here later.
  ========================================================= */

  const apiUpdateItem = async () => {
    setLoading(true);

    try {
      const BASE_URL = process.env.REACT_APP_SERVER_SCRIPT_HOST;

      const clientId = companyData?.clientId;

      if (!clientId) {
        throw new Error("Client ID is missing.");
      }

      if (!itemsData?._id) {
        throw new Error("Item ID is missing.");
      }

      const payload = buildEditItemPayload(formData);

      console.log("Update Item Payload:", payload);

      const { data } = await axios.patch(
        `${BASE_URL}/client/${companyData?.clientId}/company-items/dispatch-edit-item/${itemsData?.sku}`,
        payload,
      );

      console.log("Update Item Response:", data);

      if (data?.status === 200) {
        setAlert({
          message:
            data?.message ||
            "Your item was updated successfully. All changes have been saved.",
          type: "added",
        });

        setIsDirty(false);

        if (typeof onSave === "function") {
          onSave(data);
        }

        return data;
      }

      enqueueSnackbar(data?.message || "Failed to update item.", {
        variant: "error",
        autoHideDuration: 3000,
      });

      return null;
    } catch (error) {
      console.error("Update Item Error:", error?.code || "REQUEST_FAILED");

      let message = "Unable to update item. Please try again.";

      if (error.code === "ERR_NETWORK") {
        message =
          "No internet connection. Please check your network and try again.";
      } else if (error.response?.data?.message) {
        message = error.response.data.message;
      } else if (error.message) {
        message = error.message;
      }

      enqueueSnackbar(message, {
        variant: "error",
        autoHideDuration: 4000,
      });

      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    if (!validateForm()) {
      return;
    }
    await apiUpdateItem();
  };
  /* =========================================================
     IMAGE
  ========================================================= */

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files || []);

    const imageUrls = files.map((file) => URL.createObjectURL(file));

    setFormData((previous) => ({
      ...previous,
      images: [...previous.images, ...imageUrls],
    }));

    setIsDirty(true);
  };

  const removeImage = (index) => {
    setFormData((previous) => ({
      ...previous,

      images: previous.images.filter((_, imageIndex) => imageIndex !== index),
    }));

    setIsDirty(true);
  };

async function apiGetOneItems() {
  try {
    setLoading(true);

    const sku = searchParams.get("sku") || "";

    if (!sku) {
      enqueueSnackbar("SKU is missing.", {
        variant: "error",
        autoHideDuration: 3000,
      });

      return;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_SCRIPT_HOST}/manage_items/client/${companyData?.clientId}/items-data/api-get-item-by-sku?sku=${encodeURIComponent(sku)}`
    );

    if (response?.data?.status === 201) {
      const fetchedItem = response.data.itemFound;

      console.log("FETCHED ITEM:", fetchedItem);

      setItemsData(fetchedItem);

      setFormData(
        createEditableState(fetchedItem)
      );

      setErrors({});
      setIsDirty(false);

    } else {
      enqueueSnackbar(
        response?.data?.message ||
          "Failed to fetch item",
        {
          variant: "error",
          autoHideDuration: 3000,
        }
      );
    }

  } catch (error) {
    console.error(
      "apiGetOneItems:",
      error?.code || "REQUEST_FAILED"
    );

    enqueueSnackbar(
      error?.response?.data?.message ||
        "Server error while fetching item",
      {
        variant: "error",
        autoHideDuration: 3000,
      }
    );

  } finally {
    setLoading(false);
  }
}

  useEffect(() => {
    apiGetOneItems();
  }, []);



  if (loading || !formData || !itemsData) {
  return <IsLoading />;
}
  return ( 
        <div className="EditItemPage">
          <div className="EditItemContainer">
            {/* =====================================================
            HEADER
        ====================================================== */}

            <header className="EditItemHeader fx fx-ac fx-jb">
              <div className="fx fx-ac space1">
                <button
                  type="button"
                  className="EditItemBackButton"
                  onClick={onCancel}
                >
                  <ArrowBackRoundedIcon />
                </button>

                <div className="EditItemHeaderIcon fx fx-ac fx-jc">
                  <Inventory2OutlinedIcon />
                </div>

                <div className="fx fx-cl">
                  <div className="EditItemBreadcrumb">
                    Manage Items
                    <span>/</span>
                    Edit Item
                  </div>

                  <h1 className="EditItemTitle">
                    Edit {itemsData?.name || "Item"}
                  </h1>

                  <div className="fx fx-ac space">
                    <span className="EditItemType">{formData.itemType}</span>

                    <span className="EditItemDot" />

                    <span className="EditItemSku">
                      SKU: {formData.sku || "Not assigned"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="fx fx-ac space">
                <button
                  type="button"
                  className="EditItemResetButton"
                  onClick={handleReset}
                  disabled={!isDirty}
                >
                  <RestartAltRoundedIcon />
                  Reset
                </button>

                <button
                  type="button"
                  className="EditItemCancelButton"
                  onClick={onCancel}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  form="EditItemForm"
                  className="EditItemSaveButton"
                  onClick={() => handleSubmit()}
                >
                  <SaveOutlinedIcon />
                  Save Changes
                </button>
              </div>
            </header>

            {/* =====================================================
            EDIT FORM
        ====================================================== */}

            <section id="EditItemForm" className="EditItemContent">
              {/* ===================================================
              BASIC INFORMATION
          ==================================================== */}

              <EditSection
                icon={<InfoOutlinedIcon />}
                title="Basic Information"
                description="Core information about this inventory item."
                open={activeSections.basic}
                onClick={() => toggleSection("basic")}
              >
                <div className="EditItemGrid g g2">
                  <EditField
                    label="Item Name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    name="name"
                    placeholder="Enter item name"
                    error={errors.name}
                  />

                  <EditSelect
                    label="Item Type"
                    name="itemType"
                    value={formData.itemType}
                    onChange={handleChange}
                    options={[
                      {
                        label: "Product",
                        value: "Product",
                      },
                      {
                        label: "Service",
                        value: "Service",
                      },
                      {
                        label: "Raw Material",
                        value: "Raw Material",
                      },
                      {
                        label: "Finished Good",
                        value: "Finished Good",
                      },
                    ]}
                  />
                </div>
                <div className="EditItemGrid g g4">
                  <EditField
                    label="Brand"
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    placeholder="Enter brand"
                  />

                  <EditField
                    label="Category"
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleChange}
                    placeholder="Category ID"
                  />

                  <EditField
                    label="Supplier"
                    name="supplierId"
                    value={formData.supplierId}
                    onChange={handleChange}
                    placeholder="Supplier ID"
                  />

                  <EditSelect
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    options={[
                      {
                        label: "Active",
                        value: "active",
                      },
                      {
                        label: "Inactive",
                        value: "inactive",
                      },
                    ]}
                  />
                </div>

                <EditTextarea
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe this item..."
                />
              </EditSection>

              {/* ===================================================
              IDENTIFICATION
          ==================================================== */}

              <EditSection
                icon={<QrCode2OutlinedIcon />}
                title="Identification"
                description="Configure SKU, barcode and QR identification."
                open={activeSections.identification}
                onClick={() => toggleSection("identification")}
              >
                <div className="EditItemGrid g g3">
                  <EditField
                    label="SKU"
                    required
                    name="sku"
                    value={formData.sku}
                    onChange={handleChange}
                    placeholder="e.g. SOPSIT0001"
                    error={errors.sku}
                  />

                  <EditField
                    label="Barcode"
                    name="barcode"
                    value={formData.barcode}
                    onChange={handleChange}
                    placeholder="Enter barcode"
                  />

                  <EditField
                    label="QR Code"
                    name="qrcode"
                    value={formData.qrcode}
                    onChange={handleChange}
                    placeholder="Enter QR code"
                  />
                </div>
              </EditSection>

              {/* ===================================================
              PRICING
          ==================================================== */}

              <EditSection
                icon={<PaymentsOutlinedIcon />}
                title="Pricing & Tax"
                description="Configure cost, selling price and tax information."
                open={activeSections.pricing}
                onClick={() => toggleSection("pricing")}
              >
                <div className="EditItemGrid g g4">
                  <EditField
                    label="Cost Price"
                    type="number"
                    value={formData.pricing.costPrice}
                    onChange={(event) =>
                      handleNestedChange(
                        "pricing",
                        "costPrice",
                        event.target.value,
                      )
                    }
                    placeholder="0.00"
                    prefix={formData.pricing.currency}
                    error={errors["pricing.costPrice"]}
                  />

                  <EditField
                    label="Selling Price"
                    required
                    type="number"
                    value={formData.pricing.sellingPrice}
                    onChange={(event) =>
                      handleNestedChange(
                        "pricing",
                        "sellingPrice",
                        event.target.value,
                      )
                    }
                    placeholder="0.00"
                    prefix={formData.pricing.currency}
                    error={errors["pricing.sellingPrice"]}
                  />

                  <EditField
                    label="Tax Rate"
                    type="number"
                    value={formData.pricing.taxRate}
                    onChange={(event) =>
                      handleNestedChange(
                        "pricing",
                        "taxRate",
                        event.target.value,
                      )
                    }
                    placeholder="0"
                    suffix="%"
                  />

                  <EditSelect
                    label="Currency"
                    value={formData.pricing.currency}
                    onChange={(event) =>
                      handleNestedChange(
                        "pricing",
                        "currency",
                        event.target.value,
                      )
                    }
                    options={[
                      {
                        label: "NGN — Nigerian Naira",
                        value: "NGN",
                      },
                      {
                        label: "USD — US Dollar",
                        value: "USD",
                      },
                      {
                        label: "GBP — British Pound",
                        value: "GBP",
                      },
                      {
                        label: "EUR — Euro",
                        value: "EUR",
                      },
                    ]}
                  />
                </div>

                <div className="EditPricingPreview">
                  <div>
                    <span>Estimated Unit Profit</span>

                    <strong>
                      {formData.pricing.currency}{" "}
                      {(
                        Number(formData.pricing.sellingPrice || 0) -
                        Number(formData.pricing.costPrice || 0)
                      ).toLocaleString()}
                    </strong>
                  </div>

                  <div>
                    <span>Margin</span>

                    <strong>
                      {Number(formData.pricing.sellingPrice) > 0
                        ? ((Number(formData.pricing.sellingPrice) -
                            Number(formData.pricing.costPrice)) /
                            Number(formData.pricing.sellingPrice)) *
                          100
                        : 0}
                      %
                    </strong>
                  </div>
                </div>
              </EditSection>

              {/* ===================================================
              UNITS
          ==================================================== */}

              <EditSection
                icon={<SettingsOutlinedIcon />}
                title="Units & Conversion"
                description="Define how this item is purchased, stored and sold."
                open={activeSections.units}
                onClick={() => toggleSection("units")}
              >
                <div className="EditItemGrid g g4">
                  <EditField
                    label="Base Unit"
                    value={formData.units.baseUnit}
                    onChange={(event) =>
                      handleNestedChange(
                        "units",
                        "baseUnit",
                        event.target.value,
                      )
                    }
                    placeholder="e.g. Piece"
                  />

                  <EditField
                    label="Purchase Unit"
                    value={formData.units.purchaseUnit}
                    onChange={(event) =>
                      handleNestedChange(
                        "units",
                        "purchaseUnit",
                        event.target.value,
                      )
                    }
                    placeholder="e.g. Box"
                  />

                  <EditField
                    label="Conversion Rate"
                    type="number"
                    value={formData.units.conversionRate}
                    onChange={(event) =>
                      handleNestedChange(
                        "units",
                        "conversionRate",
                        event.target.value,
                      )
                    }
                    placeholder="1"
                  />

                  <EditField
                    label="Sales Unit"
                    value={formData.units.salesUnit}
                    onChange={(event) =>
                      handleNestedChange(
                        "units",
                        "salesUnit",
                        event.target.value,
                      )
                    }
                    placeholder="e.g. Set"
                  />
                </div>
              </EditSection>

              {/* ===================================================
              DIMENSIONS
          ==================================================== */}

              <EditSection
                icon={<StraightenOutlinedIcon />}
                title="Dimensions"
                description="Configure physical dimensions and measurement information."
                open={activeSections.dimensions}
                onClick={() => toggleSection("dimensions")}
              >
                <div className="EditItemGrid g g5">
                  <EditField
                    label="Measurement Unit"
                    value={formData.dimensions.unitsOfMeasure}
                    onChange={(event) =>
                      handleNestedChange(
                        "dimensions",
                        "unitsOfMeasure",
                        event.target.value,
                      )
                    }
                    placeholder="cm"
                  />

                  <EditField
                    label="Weight"
                    type="number"
                    value={formData.dimensions.weight}
                    onChange={(event) =>
                      handleNestedChange(
                        "dimensions",
                        "weight",
                        event.target.value,
                      )
                    }
                    placeholder="0"
                  />

                  <EditField
                    label="Length"
                    type="number"
                    value={formData.dimensions.length}
                    onChange={(event) =>
                      handleNestedChange(
                        "dimensions",
                        "length",
                        event.target.value,
                      )
                    }
                    placeholder="0"
                  />

                  <EditField
                    label="Width"
                    type="number"
                    value={formData.dimensions.width}
                    onChange={(event) =>
                      handleNestedChange(
                        "dimensions",
                        "width",
                        event.target.value,
                      )
                    }
                    placeholder="0"
                  />

                  <EditField
                    label="Height"
                    type="number"
                    value={formData.dimensions.height}
                    onChange={(event) =>
                      handleNestedChange(
                        "dimensions",
                        "height",
                        event.target.value,
                      )
                    }
                    placeholder="0"
                  />
                </div>
              </EditSection>

              {/* ===================================================
              INVENTORY CONFIGURATION
          ==================================================== */}

              <EditSection
                icon={<WarehouseOutlinedIcon />}
                title="Inventory Configuration"
                description="Configure inventory tracking, reorder rules and batch settings."
                open={activeSections.inventory}
                onClick={() => toggleSection("inventory")}
              >
                <div className="EditToggleCard">
                  <div className="fx fx-ac fx-jb">
                    <div className="fx fx-ac space1">
                      <div className="EditToggleIcon">
                        <WarehouseOutlinedIcon />
                      </div>

                      <div className="fx fx-cl">
                        <strong>Track Inventory</strong>

                        <span>
                          Automatically track stock movements for this item.
                        </span>
                      </div>
                    </div>

                    <label className="EditSwitch">
                      <input
                        type="checkbox"
                        name="trackInventory"
                        checked={formData.trackInventory}
                        onChange={handleChange}
                      />

                      <span className="EditSwitchSlider" />
                    </label>
                  </div>
                </div>

                {formData.inventories.map((inventory, inventoryIndex) => (
                  <div
                    className="EditInventoryCard"
                    key={inventory.inventoryId || inventoryIndex}
                  >
                    <div className="EditInventoryCardHeader fx fx-ac fx-jb">
                      <div className="fx fx-ac space">
                        <div className="EditInventoryIcon">
                          <WarehouseOutlinedIcon />
                        </div>

                        <div className="fx fx-cl">
                          <strong>
                            {inventory.inventoryId || "Inventory Location"}
                          </strong>

                          <span>Inventory configuration</span>
                        </div>
                      </div>
                    </div>

                    <div className="EditInventorySubTitle">
                      Reorder Configuration
                    </div>

                    <div className="EditItemGrid g g3">
                      <EditField
                        label="Minimum Level"
                        type="number"
                        value={inventory.stock.minLevel}
                        onChange={(event) =>
                          handleInventoryChange(
                            inventoryIndex,
                            "stock",
                            "minLevel",
                            event.target.value,
                          )
                        }
                        placeholder="0"
                      />

                      <EditField
                        label="Reorder Level"
                        type="number"
                        value={inventory.stock.reorderLevel}
                        onChange={(event) =>
                          handleInventoryChange(
                            inventoryIndex,
                            "stock",
                            "reorderLevel",
                            event.target.value,
                          )
                        }
                        placeholder="0"
                      />

                      <EditField
                        label="Reorder Quantity"
                        type="number"
                        value={inventory.stock.reorderQuantity}
                        onChange={(event) =>
                          handleInventoryChange(
                            inventoryIndex,
                            "stock",
                            "reorderQuantity",
                            event.target.value,
                          )
                        }
                        placeholder="0"
                      />
                    </div>

                    <div className="EditInventorySubTitle">Batch Tracking</div>

                    <div className="EditToggleGrid g g2">
                      <ToggleField
                        label="Batch Tracking"
                        description="Track inventory using batch numbers."
                        checked={inventory.batchTracking.batchTracking}
                        onChange={(event) =>
                          handleInventoryChange(
                            inventoryIndex,
                            "batchTracking",
                            "batchTracking",
                            event.target.checked,
                          )
                        }
                      />

                      <ToggleField
                        label="Expiry Tracking"
                        description="Track expiration dates for inventory."
                        checked={inventory.batchTracking.expiryTracking}
                        onChange={(event) =>
                          handleInventoryChange(
                            inventoryIndex,
                            "batchTracking",
                            "expiryTracking",
                            event.target.checked,
                          )
                        }
                      />
                    </div>

                    <div className="EditItemGrid g g2">
                      <EditSelect
                        label="Tracking Method"
                        value={inventory.batchTracking.trackingMethod}
                        onChange={(event) =>
                          handleInventoryChange(
                            inventoryIndex,
                            "batchTracking",
                            "trackingMethod",
                            event.target.value,
                          )
                        }
                        options={[
                          {
                            label: "FEFO",
                            value: "FEFO",
                          },
                          {
                            label: "FIFO",
                            value: "FIFO",
                          },
                          {
                            label: "LIFO",
                            value: "LIFO",
                          },
                        ]}
                      />
                    </div>
                  </div>
                ))}
              </EditSection>

              {/* ===================================================
              IMAGES
          ==================================================== */}

              <EditSection
                icon={<ImageOutlinedIcon />}
                title="Item Images"
                description="Manage images associated with this item."
                open={activeSections.images}
                onClick={() => toggleSection("images")}
              >
                <div className="EditImageUpload">
                  <label className="EditUploadButton">
                    <AddRoundedIcon />

                    <span>Add Images</span>

                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                    />
                  </label>

                  <span className="EditUploadHint">PNG, JPG or WEBP</span>
                </div>

                {formData.images.length > 0 && (
                  <div className="EditImageGrid">
                    {formData.images.map((image, index) => (
                      <div className="EditImageCard" key={`${image}-${index}`}>
                        <img
                          src={image}
                          alt={`${formData.name} ${index + 1}`}
                        />

                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="EditImageDelete"
                        >
                          <DeleteOutlineRoundedIcon />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </EditSection>

              {/* ===================================================
              BOTTOM ACTION
          ==================================================== */}

              <div className="EditItemBottomActions fx fx-jb fx-ac">
                <div className="fx fx-ac space">
                  <InfoOutlinedIcon />

                  <span>
                    Changes will only take effect after you save this item.
                  </span>
                </div>

                <div className="fx fx-ac space">
                  <button
                    type="button"
                    className="EditItemCancelButton"
                    onClick={onCancel}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="EditItemSaveButton"
                    onClick={() => handleSubmit()}
                  >
                    <SaveOutlinedIcon />
                    Save Changes
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>  
  );
};

/* =========================================================
   SECTION
========================================================= */

const EditSection = ({ icon, title, description, open, onClick, children }) => {
  return (
    <section className="EditItemSection">
      <button
        type="button"
        className="EditItemSectionHeader fx fx-ac fx-jb"
        onClick={onClick}
      >
        <div className="fx fx-ac space1">
          <div className="EditItemSectionIcon">{icon}</div>

          <div className="fx fx-cl">
            <strong>{title}</strong>

            <span>{description}</span>
          </div>
        </div>

        <KeyboardArrowDownRoundedIcon
          className={
            open ? "EditItemSectionArrow active" : "EditItemSectionArrow"
          }
        />
      </button>

      {open && <div className="EditItemSectionBody">{children}</div>}
    </section>
  );
};

/* =========================================================
   INPUT
========================================================= */

const EditField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  required = false,
  error,
  prefix,
  suffix,
}) => {
  return (
    <div className="EditField">
      <label>
        {label}

        {required && <span className="EditRequired">*</span>}
      </label>

      <div className={error ? "EditInputWrapper error" : "EditInputWrapper"}>
        {prefix && <span className="EditInputPrefix">{prefix}</span>}

        <input
          type={type}
          name={name}
          value={value ?? ""}
          onChange={onChange}
          placeholder={placeholder}
        />

        {suffix && <span className="EditInputSuffix">{suffix}</span>}
      </div>

      {error && <span className="EditFieldError">{error}</span>}
    </div>
  );
};

/* =========================================================
   SELECT
========================================================= */

const EditSelect = ({ label, name, value, onChange, options }) => {
  return (
    <div className="EditField">
      <label>{label}</label>

      <div className="EditInputWrapper">
        <select name={name} value={value ?? ""} onChange={onChange}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

/* =========================================================
   TEXTAREA
========================================================= */

const EditTextarea = ({ label, name, value, onChange, placeholder }) => {
  return (
    <div className="EditField EditTextareaField">
      <label>{label}</label>

      <textarea
        name={name}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        rows={5}
      />
    </div>
  );
};

/* =========================================================
   TOGGLE
========================================================= */

const ToggleField = ({ label, description, checked, onChange }) => {
  return (
    <div className="EditToggleField">
      <div className="fx fx-ac fx-jb">
        <div className="fx fx-cl">
          <strong>{label}</strong>

          <span>{description}</span>
        </div>

        <label className="EditSwitch">
          <input type="checkbox" checked={checked} onChange={onChange} />

          <span className="EditSwitchSlider" />
        </label>
      </div>
    </div>
  );
};

export default EditItem;
